// @ts-check
import { useSelector } from 'react-redux';
import Icon from 'components/base/Icon';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import SearchInput from 'components/SearchInput';
import { activeGithubIntegrationsSelector } from 'state/selectors/sensors-authorizations';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import { generateTotalGroupAndTeamMsg } from 'common/utils/utils';
import useRBACPermissions from 'views/root/views/home/hooks/useRBACPermissions';
import debounce from 'lodash/debounce';
import { memo, useEffect } from 'react';
import {
  LBButton,
  LBTooltipProvider,
  LBTooltip,
  LBTooltipTrigger,
  LBTooltipContent
} from '@linearb/buzz';

const BtnCreateGroupOfTeams = ({ onClick }) => (
  <LBTooltipProvider>
    <LBTooltip>
      <LBTooltipTrigger>
        <LBButton
          data-testid='btnCreateGroupOfTeams'
          data-cy='btn-create-team'
          onClick={onClick}
          variant='outline'
        >
          <Icon name='Add' /> Add a Group
        </LBButton>
      </LBTooltipTrigger>
      <LBTooltipContent>
        Groups may include teams, other groups, or individual contributors
      </LBTooltipContent>
    </LBTooltip>
  </LBTooltipProvider>
);

const BtnCreateTeam = ({ onClick }) => (
  <LBTooltipProvider>
    <LBTooltip>
      <LBTooltipTrigger>
        <LBButton variant='outline' data-cy='btn-create-team' onClick={onClick}>
          <Icon name='Add' /> Add a Team
        </LBButton>
      </LBTooltipTrigger>
      <LBTooltipContent>Teams include contributors</LBTooltipContent>
    </LBTooltip>
  </LBTooltipProvider>
);

const BtnSyncGithub = ({ onClick }) => (
  <LBButton data-testid='btnSyncTeamWithGithub' onClick={onClick}>
    <Icon color='white' name='GithubIcon' size='smaller' /> Sync with GitHub
  </LBButton>
);

const TeamsHeader = ({
  teamsWithExtraData,
  searchInput,
  setSearchInput,
  onConfigureTeam,
  toggleSyncTeamsModal,
  onConfigureGroup
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();
  const contributorsAndTeamsSettingsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.contributorsAndTeamsSettingsEnabled
  });
  const activeGithubSensors = useSelector(activeGithubIntegrationsSelector);
  const isShowSyncGithubBtn =
    contributorsAndTeamsSettingsEnabled && activeGithubSensors?.length > 0;
  const totalGroupsAndTeamsMsg =
    generateTotalGroupAndTeamMsg(teamsWithExtraData);
  const { isPermitted } = useRBACPermissions();

  const handleSearch = debounce(value => {
    setSearchInput(value);
  }, 100);

  useEffect(() => {
    return () => {
      handleSearch?.cancel();
    };
  }, []);

  return (
    <Flex direction='column' mb='medium' role='teams-header'>
      <Flex mb='medium' align='center'>
        {totalGroupsAndTeamsMsg && (
          <Text size='x-medium' ml='1rem' color='shuttleGray'>
            Total {totalGroupsAndTeamsMsg}
          </Text>
        )}
      </Flex>
      <Flex align='center'>
        {/* @ts-expect-error*/}
        <SearchInput
          width='20%'
          height='3rem'
          placeholder='Search for team'
          defaultValue={searchInput}
          onChange={handleSearch}
        />
        <div className='flex justify-end items-center gap-2 w-full'>
          {isPermitted && <BtnCreateTeam onClick={onConfigureTeam} />}
          {isPermitted && <BtnCreateGroupOfTeams onClick={onConfigureGroup} />}

          {!!isShowSyncGithubBtn && isPermitted && (
            <BtnSyncGithub onClick={toggleSyncTeamsModal} />
          )}
        </div>
      </Flex>
    </Flex>
  );
};

export default memo(TeamsHeader);
